import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Typography, Box, List, ListItem, Paper, Select, MenuItem,
  FormControl, Skeleton, IconButton, Tooltip, Badge
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import BaseCard from './BaseCard';
import OperationalAlertsSettingsModal from './OperationalAlertsSettingsModal';
import FilterModal from './FilterModal';
import { DESERT_FLOWER, PEANUT_BUTTER_ORANGE, COOL_GREEN } from '../../../dashboardColors';
import './NotificationSection.scss';

const NotificationSection = ({
  notifications,
  onAlertClick,
  onEventStateChange,
  onEventPriorityChange,
  isLoading = false,
  eventStateOptions = [],
  eventPriorityOptions = [],
  operationalAlertsSettings,
  onUpdateOperationalAlertsSettings,
  onFiltersChange,
  sharedPriorityFilter = [],
  sharedEventStateFilter = [],
  filtersInitialized = false
}, context) => {
  // Local state for event state and priority changes
  const [eventStates, setEventStates] = useState({});
  const [eventPriorities, setEventPriorities] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState([]);
  const [selectedEventStateFilter, setSelectedEventStateFilter] = useState([]);

  // Sorting state: column key and direction ('asc' or 'desc')
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Calculate default event states once
  const defaultEventStates = useMemo(() =>
    eventStateOptions
      .filter(option => [0, 1, 2].includes(option.value)) // 'new event', 'viewed', 'workorder'
      .map(option => option.value),
    [eventStateOptions]
  );

  // Initialize filters on mount
  useEffect(() => {
    if (filtersInitialized) {
      // Use shared filters from parent (user has changed them)
      setSelectedPriorityFilter(sharedPriorityFilter);
      setSelectedEventStateFilter(sharedEventStateFilter);
    } else if (operationalAlertsSettings?.opr_alerts_event_states) {
      // Use saved settings
      setSelectedEventStateFilter(operationalAlertsSettings.opr_alerts_event_states);
    } else if (defaultEventStates.length > 0) {
      // Use defaults
      setSelectedEventStateFilter(defaultEventStates);
    }
  }, []); // Only run on mount

  // Notify parent when filters change (skip initial notification)
  const prevFiltersRef = useRef({ priority: selectedPriorityFilter, eventState: selectedEventStateFilter });
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const priorityChanged = JSON.stringify(prevFilters.priority) !== JSON.stringify(selectedPriorityFilter);
    const eventStateChanged = JSON.stringify(prevFilters.eventState) !== JSON.stringify(selectedEventStateFilter);

    if ((priorityChanged || eventStateChanged) && onFiltersChange) {
      onFiltersChange(selectedPriorityFilter, selectedEventStateFilter);
      prevFiltersRef.current = { priority: selectedPriorityFilter, eventState: selectedEventStateFilter };
    }
  }, [selectedPriorityFilter, selectedEventStateFilter, onFiltersChange]);

  // Helper: Check if alert priority matches filter
  const matchesPriorityFilter = (alert, filters) => {
    const priorityText = (alert.priority || '').toLowerCase();
    return filters.some(filter => priorityText.includes(filter === 'medium' ? 'med' : filter));
  };

  // Filter and sort notifications
  const sortedNotifications = useMemo(() => {
    if (!notifications?.length) return [];

    let filtered = notifications;

    // Filter by priority
    if (selectedPriorityFilter.length > 0) {
      filtered = filtered.filter(alert => matchesPriorityFilter(alert, selectedPriorityFilter));
    }

    // Filter by event state
    if (selectedEventStateFilter.length > 0) {
      filtered = filtered.filter(alert => {
        const alertEventState = eventStates[alert.id] ?? alert.eventStateValue ?? 0;
        return selectedEventStateFilter.includes(alertEventState);
      });
    }

    // Sort based on sortConfig or default to priority
    const sorted = [...filtered];

    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'alertId':
            aValue = a.alertId || 0;
            bValue = b.alertId || 0;
            break;
          case 'priority':
            // Lower priorityValue = higher priority (0=high, 1=medium, 2=low)
            aValue = eventPriorities[a.id] ?? a.priorityValue ?? 999;
            bValue = eventPriorities[b.id] ?? b.priorityValue ?? 999;
            break;
          case 'alertStatus':
            aValue = (a.alertStatus || '').toLowerCase();
            bValue = (b.alertStatus || '').toLowerCase();
            break;
          case 'probability':
            aValue = a.probability || 0;
            bValue = b.probability || 0;
            break;
          case 'eventState':
            aValue = eventStates[a.id] ?? a.eventStateValue ?? 0;
            bValue = eventStates[b.id] ?? b.eventStateValue ?? 0;
            break;
          case 'comment':
            aValue = (a.comment || '').toLowerCase();
            bValue = (b.comment || '').toLowerCase();
            break;
          default:
            return 0;
        }

        // Handle numeric vs string comparison
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        // String comparison
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sort by priority type
      const priorityOrder = { class_a: 1, high_probability: 2, long_time: 3 };
      sorted.sort((a, b) => (priorityOrder[a.type] || 999) - (priorityOrder[b.type] || 999));
    }

    return sorted;
  }, [notifications, selectedPriorityFilter, selectedEventStateFilter, eventStates, eventPriorities, sortConfig]);

  const getIndicatorIcon = (indicator) => {
    if (!indicator) return null;
    
    const indicatorLower = indicator.toLowerCase();
    const iconProps = { fontSize: 20 };
    
    switch (indicatorLower) {
      case 'burst':
      case 'pipe burst':
        return <WarningIcon sx={{ ...iconProps, color: '#d32f2f' }} />;
      case 'growing up':
        return <TrendingUpIcon sx={{ ...iconProps, color: '#d32f2f' }} />; // Red for growing up (bad)
      case 'growing down':
      case 'fading':
        return <TrendingDownIcon sx={{ ...iconProps, color: '#4caf50' }} />; // Green for growing down (good)
      case 'no event':
        return <CloseIcon sx={{ ...iconProps, color: '#9e9e9e' }} />;
      default:
        return null;
    }
  };
  
  const truncateText = (text, maxLength = 40) => {
    if (!text || text.length <= maxLength) return text || '';
    return text.substring(0, maxLength) + '...';
  };

  const handleEventStateChange = (alertId, newState) => {
    setEventStates(prev => ({ ...prev, [alertId]: newState }));
    if (onEventStateChange) {
      onEventStateChange(alertId, newState);
    }
  };

  const handleEventPriorityChange = (alertId, newPriority) => {
    setEventPriorities(prev => ({ ...prev, [alertId]: newPriority }));
    if (onEventPriorityChange) {
      onEventPriorityChange(alertId, newPriority);
    }
  };

  const handleSettingsClick = (e) => {
    e.stopPropagation();
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => setSettingsOpen(false);

  const handleSettingsSave = (newSettings) => {
    onUpdateOperationalAlertsSettings?.(newSettings);
  };

  const handleFilterClick = (e) => {
    e?.stopPropagation();
    setFilterDialogOpen(true);
  };

  const handleFilterClose = () => {
    setFilterDialogOpen(false);
  };

  const handleFilterSave = (filters) => {
    setSelectedPriorityFilter(filters.priorities || []);
    setSelectedEventStateFilter(filters.eventStates || []);
    setFilterDialogOpen(false);
  };

  // Check if any filters are active
  const hasActiveFilters = selectedPriorityFilter.length > 0 || selectedEventStateFilter.length > 0;

  // Handle dropdown open/close state
  const handleDropdownOpen = (dropdownKey, isOpen) => {
    setOpenDropdowns(prev => ({ ...prev, [dropdownKey]: isOpen }));
  };

  // Close all dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideDropdown = event.target.closest('.MuiSelect-select') ||
                                    event.target.closest('.MuiMenuItem-root') ||
                                    event.target.closest('.MuiPopover-paper');
      if (!isClickInsideDropdown) {
        setOpenDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle column header click for sorting
  const handleSort = (columnKey) => {
    setSortConfig(prev => {
      if (prev.key === columnKey) {
        // Toggle direction if same column
        return { key: columnKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      // New column, default to ascending
      return { key: columnKey, direction: 'asc' };
    });
  };

  // Render sortable column header
  const renderSortableHeader = (columnKey, label) => {
    const isSorted = sortConfig.key === columnKey;
    return (
      <th
        className={`sortable-header ${isSorted ? 'sorted' : ''}`}
        onClick={() => handleSort(columnKey)}
      >
        <span className="header-content">
          {label}
          <span className={`sort-icon ${isSorted ? 'active' : ''}`}>
            {isSorted && sortConfig.direction === 'desc' ? (
              <ArrowDownwardIcon fontSize="small" />
            ) : (
              <ArrowUpwardIcon fontSize="small" />
            )}
          </span>
        </span>
      </th>
    );
  };

  return (
    <>
      <Box position="relative">
        <BaseCard
          title={context.t('high_priority_leak_alerts')}
          icon={<NotificationsIcon />}
          className="notification-section"
        >
      <Box className="notification-table-container">
        {isLoading ? (
          // Show loading skeleton
          <List className="notification-list">
            {[1, 2, 3].map((index) => (
              <ListItem key={`skeleton-${index}`} className="alert-row" disablePadding>
                <Paper elevation={1} className="alert-content">
                  <Box className="alert-row-content">
                    <Box className="alert-left-section">
                      <Skeleton variant="circular" width={28} height={28} />
                      <Skeleton variant="text" width={80} height={24} />
                    </Box>
                    <Box className="alert-middle-section">
                      <Skeleton variant="rectangular" width={200} height={20} sx={{ mr: 1 }} />
                      <Skeleton variant="rectangular" width={150} height={20} />
                    </Box>
                  </Box>
                </Paper>
              </ListItem>
            ))}
          </List>
        ) : sortedNotifications.length > 0 ? (
          <table className="notification-table">
            <thead>
              <tr>
                <th className="indicator-header" style={{ minWidth: '40px', width: '40px' }}>&nbsp;</th>
                {renderSortableHeader('alertId', context.t('alert_id'))}
                {renderSortableHeader('priority', context.t('event_priority'))}
                {renderSortableHeader('alertStatus', context.t('alert_status'))}
                {renderSortableHeader('probability', context.t('probability'))}
                {renderSortableHeader('eventState', context.t('event_state'))}
                {renderSortableHeader('comment', context.t('comment'))}
              </tr>
            </thead>
            <tbody>
              {sortedNotifications.map((alert) => (
                  <tr
                    key={alert.id}
                    className="alert-row"
                    onClick={() => onAlertClick && onAlertClick(alert.alertId)}
                  >
                    <td className="indicator-cell" style={{ minWidth: '40px', width: '40px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {getIndicatorIcon(alert.indicator)}
                      </Box>
                    </td>
                    <td className="alert-id-cell">
                      <Typography variant="subtitle1" className="alert-id" fontWeight="bold">
                        {alert.alertId}
                      </Typography>
                    </td>
                    <td className="priority-cell">
                      <FormControl size="small" sx={{ minWidth: 115 }}>
                        <Select
                          value={eventPriorities[alert.id] !== undefined
                                 ? eventPriorities[alert.id]
                                 : (typeof alert.priorityValue === 'number' ? alert.priorityValue : '')}
                          onChange={(e) => handleEventPriorityChange(alert.id, e.target.value)}
                          className="priority-dropdown"
                          open={openDropdowns[`priority-${alert.id}`] || false}
                          onOpen={() => handleDropdownOpen(`priority-${alert.id}`, true)}
                          onClose={() => handleDropdownOpen(`priority-${alert.id}`, false)}
                          MenuProps={{
                            disablePortal: false,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left',
                            },
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                zIndex: 9999
                              },
                              onClick: (e) => e.stopPropagation()
                            }
                          }}
                          sx={{
                            fontSize: '1.2rem',
                            fontWeight: 600,
                            '& .MuiSelect-select': {
                              padding: '4px 8px',
                              textAlign: 'center',
                              color: (() => {
                                // Use numeric priorityValue for color
                                // Database: 0 = low, 1 = medium, 2 = high
                                // Colors: low = green, medium = orange, high = red
                                const currentPriority = eventPriorities[alert.id] !== undefined
                                  ? eventPriorities[alert.id]
                                  : alert.priorityValue;
                                if (currentPriority === 0) return COOL_GREEN;       // low = green
                                if (currentPriority === 1) return PEANUT_BUTTER_ORANGE;  // medium = orange
                                if (currentPriority === 2) return DESERT_FLOWER;   // high = red
                                return '#666'; // Default gray
                              })(),
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {eventPriorityOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </td>
                    <td className="status-cell">
                      <Typography
                        variant="body1"
                        className="status-badge"
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {alert.alertStatus}
                      </Typography>
                    </td>
                    <td className="probability-cell">
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        {alert.probability}
                      </Typography>
                    </td>
                    <td className="eventstate-cell">
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={eventStates[alert.id] !== undefined ? eventStates[alert.id] : (typeof alert.eventStateValue === 'number' ? alert.eventStateValue : 0)}
                          onChange={(e) => handleEventStateChange(alert.id, e.target.value)}
                          className="event-state-dropdown"
                          open={openDropdowns[`state-${alert.id}`] || false}
                          onOpen={() => handleDropdownOpen(`state-${alert.id}`, true)}
                          onClose={() => handleDropdownOpen(`state-${alert.id}`, false)}
                          MenuProps={{
                            disablePortal: false,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left',
                            },
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                zIndex: 9999
                              },
                              onClick: (e) => e.stopPropagation()
                            }
                          }}
                          sx={{
                            fontSize: '1.3rem',
                            fontWeight: 500,
                            color: '#666',
                            '& .MuiSelect-select': {
                              padding: '4px 8px',
                              textAlign: 'center',
                            }
                          }}
                          onClick={(e) => e.stopPropagation()} // Prevent triggering alert click
                        >
                          {eventStateOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </td>
                    <td className="comment-cell">
                      <Typography variant="body2" className="comment-text" title={alert.comment || ''} sx={{ fontWeight: 500 }}>
                        {alert.comment ? truncateText(alert.comment, 50) : '-'}
                      </Typography>
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Typography variant="body2" color="textSecondary" align="center" py={2}>
            {context.t('no_high_priority_alerts')}
          </Typography>
        )}
      </Box>
    </BaseCard>

        <Box sx={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          display: 'flex',
          gap: 1
        }}>
          <Tooltip title={context.t('operational_alerts_settings')}>
            <IconButton
              onClick={handleSettingsClick}
              size="small"
              sx={{
                backgroundColor: 'white',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                padding: '4px',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={context.t('filters') || 'Filters'}>
            <IconButton
              onClick={handleFilterClick}
              size="small"
              sx={{
                backgroundColor: hasActiveFilters ? '#e3f2fd' : 'white',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                padding: '4px',
                '&:hover': {
                  backgroundColor: hasActiveFilters ? '#bbdefb' : '#f5f5f5'
                }
              }}
            >
              <Badge color="primary" variant="dot" invisible={!hasActiveFilters}>
                <FilterListIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <OperationalAlertsSettingsModal
        open={settingsOpen}
        onClose={handleSettingsClose}
        settings={operationalAlertsSettings}
        eventStateOptions={eventStateOptions}
        onSave={handleSettingsSave}
        t={context.t}
      />

      <FilterModal
        open={filterDialogOpen}
        onClose={handleFilterClose}
        currentFilter={selectedPriorityFilter}
        currentEventStateFilter={selectedEventStateFilter}
        eventStateOptions={eventStateOptions}
        onSave={handleFilterSave}
        t={context.t}
      />
    </>
  );
};

NotificationSection.propTypes = {
  notifications: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    alertId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    priority: PropTypes.string,
    indicator: PropTypes.string,
    alertStatus: PropTypes.string,
    probability: PropTypes.number,
    openType: PropTypes.string,
    eventState: PropTypes.string,
    eventStateValue: PropTypes.number,
    comment: PropTypes.string,
    location: PropTypes.string,
    daysOpen: PropTypes.number
  })),
  onAlertClick: PropTypes.func,
  onEventStateChange: PropTypes.func,
  isLoading: PropTypes.bool,
  eventStateOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired
  })),
  operationalAlertsSettings: PropTypes.object,
  onUpdateOperationalAlertsSettings: PropTypes.func,
  onFiltersChange: PropTypes.func,
  sharedPriorityFilter: PropTypes.array,
  sharedEventStateFilter: PropTypes.array,
  filtersInitialized: PropTypes.bool
};

NotificationSection.contextTypes = {
  t: PropTypes.func.isRequired
};

export default NotificationSection;